<template>
  <div class="erp-entity-settings card-block">
    <div class="settings-header">
      <h5 class="settings-title">{{ $t("tools.erp_integration.entity_settings") }}</h5>
      <div class="settings-actions">
        <button
          v-if="isEndpointMode"
          class="btn btn-sm btn-outline-secondary"
          :disabled="busyDetect"
          @click="emitDetectSchema"
        >
          {{ $t("tools.erp_integration.detect_schema") }}
        </button>
        <button class="btn btn-sm btn-outline-primary" :disabled="busySave" @click="emitSave">
          {{ $t("common.save") }}
        </button>
      </div>
    </div>

    <div class="form-row mb-3">
      <div class="col-md-4">
        <label class="control-label">{{ $t("tools.erp_integration.data_source_type") }}</label>
        <select v-model="dataSourceType" class="form-control">
          <option value="ENDPOINT">{{ $t("tools.erp_integration.data_source_endpoint") }}</option>
          <option value="STATIC">{{ $t("tools.erp_integration.data_source_static") }}</option>
        </select>
      </div>
    </div>

    <div class="form-row mb-3" v-if="isEndpointMode">
      <div class="col-md-8">
        <label class="control-label">{{ $t("tools.erp_integration.erp_endpoint") }}</label>
        <input
          v-model="endpointPath"
          class="form-control"
          type="text"
          :placeholder="$t('tools.erp_integration.endpoint_placeholder')"
        />
      </div>

      <div class="col-md-4">
        <label class="control-label">{{ $t("tools.erp_integration.erp_endpoint_method") }}</label>
        <select v-model="endpointMethod" class="form-control">
          <option value="GET">{{ $t("tools.erp_integration.endpoint_method_get") }}</option>
          <option value="POST">{{ $t("tools.erp_integration.endpoint_method_post") }}</option>
        </select>
      </div>
    </div>

    <div class="form-row mb-3" v-if="isPostEndpoint">
      <div class="col-12">
        <label class="control-label">{{ $t("tools.erp_integration.erp_endpoint_body") }}</label>
        <textarea
          v-model="endpointBody"
          class="form-control endpoint-body"
          rows="6"
          :placeholder="$t('tools.erp_integration.endpoint_body_placeholder')"
        />
        <small class="form-text text-muted">{{ $t("tools.erp_integration.endpoint_body_hint") }}</small>
      </div>
    </div>

    <div class="form-row mb-3" v-if="isPostEndpoint">
      <div class="col-md-6">
        <div class="custom-control custom-checkbox">
          <input
            type="checkbox"
            class="custom-control-input"
            id="usePagination"
            v-model="usePagination"
          />
          <label class="custom-control-label" for="usePagination">
            {{ $t("tools.erp_integration.use_pagination") }}
          </label>
          <small class="form-text text-muted d-block">{{ $t("tools.erp_integration.use_pagination_hint") }}</small>
        </div>
      </div>
    </div>

    <div v-if="!isEndpointMode" class="text-muted mb-3">
      {{ $t("tools.erp_integration.static_mode_hint") }}
    </div>

    <div class="auto-match-wrapper mb-3">
      <div class="auto-match-header">
        <h6 class="auto-match-title">{{ $t("tools.erp_integration.auto_match_rules") }}</h6>
        <button type="button" class="btn btn-sm btn-outline-secondary" @click="addAutoMatchRule">
          {{ $t("tools.erp_integration.add_auto_match_rule") }}
        </button>
      </div>

      <div v-if="autoMatchRules.length === 0" class="text-muted small">
        {{ $t("tools.erp_integration.no_auto_match_rules") }}
      </div>

      <div v-else class="auto-match-list">
        <div v-for="(rule, index) in autoMatchRules" :key="`rule-${index}`" class="auto-match-row">
          <select
            class="form-control form-control-sm"
            :value="rule.erp_field"
            @change="updateAutoMatchRule(index, 'erp_field', $event.target.value)"
          >
            <option value="">{{ $t("tools.erp_integration.select_erp_field") }}</option>
            <option v-for="field in erpFieldOptions" :key="`erp-${field}`" :value="field">
              {{ field }}
            </option>
          </select>

          <input
            class="form-control form-control-sm"
            type="text"
            :list="`sm-field-options-${index}`"
            :value="rule.sm_field"
            :placeholder="$t('tools.erp_integration.select_or_enter_sm_field')"
            @input="updateAutoMatchRule(index, 'sm_field', $event.target.value)"
          />
          <datalist :id="`sm-field-options-${index}`">
            <option v-for="field in smFieldOptions" :key="`sm-${field}`" :value="field" />
          </datalist>

          <button
            type="button"
            class="btn btn-sm btn-outline-danger"
            @click="removeAutoMatchRule(index)"
          >
            {{ $t("common.delete") }}
          </button>
        </div>
      </div>
    </div>

    <div class="schema-table-wrapper" v-if="isEndpointMode">
      <div v-if="selectedDisplayColumns.length > 0" class="selected-columns-summary">
        <strong>{{ $t("tools.erp_integration.display_columns") }}:</strong>
        <div class="selected-columns-chips">
          <button
            v-for="column in selectedDisplayColumns"
            :key="column"
            type="button"
            class="selected-column-chip"
            @click="removeDisplayColumn(column)"
          >
            {{ column }}
            <span class="selected-column-chip__remove">x</span>
          </button>
        </div>
      </div>

      <div class="schema-filter-row">
        <input
          v-model.trim="fieldSearch"
          type="text"
          class="form-control form-control-sm"
          :placeholder="$t('tools.erp_integration.mapping_search_placeholder')"
        />

        <select v-model="fieldStatusFilter" class="form-control form-control-sm schema-filter-select">
          <option value="all">{{ $t("tools.erp_integration.mapping_filter_all") }}</option>
          <option value="selected">{{ $t("tools.erp_integration.display_columns") }}</option>
          <option value="unselected">{{ $t("tools.erp_integration.mapping_filter_unassigned") }}</option>
        </select>
      </div>

      <table class="table table-sm table-bordered schema-table">
        <thead>
          <tr>
            <th>{{ $t("common.code") }}</th>
            <th class="checkbox-col">{{ $t("tools.erp_integration.primary_key_columns") }}</th>
            <th class="checkbox-col">{{ $t("tools.erp_integration.display_columns") }}</th>
            <th class="order-col">{{ $t("common.order") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="column in filteredSchemaColumns" :key="column">
            <td>{{ column }}</td>
            <td class="text-center">
              <input
                type="checkbox"
                :checked="isPrimaryColumn(column)"
                @change="togglePrimaryColumn(column, $event.target.checked)"
              />
            </td>
            <td class="text-center">
              <input
                type="checkbox"
                :checked="isDisplayColumn(column)"
                @change="toggleDisplayColumn(column, $event.target.checked)"
              />
            </td>
            <td>
              <input
                class="form-control form-control-sm"
                type="number"
                min="0"
                :disabled="!isDisplayColumn(column)"
                :value="displayOrder(column)"
                @input="setDisplayOrder(column, $event.target.value)"
              />
            </td>
          </tr>
          <tr v-if="filteredSchemaColumns.length === 0">
            <td colspan="4" class="text-center text-muted">
              {{ $t("tools.erp_integration.no_schema_columns") }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import notify from "devextreme/ui/notify";

export default {
  name: "ErpEntitySettings",
  props: {
    entity: {
      type: Object,
      default: () => null,
    },
    busySave: {
      type: Boolean,
      default: false,
    },
    busyDetect: {
      type: Boolean,
      default: false,
    },
    erpFieldOptions: {
      type: Array,
      default: () => [],
    },
    smFieldOptions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      dataSourceType: "ENDPOINT",
      endpointPath: "",
      endpointMethod: "GET",
      endpointBody: "",
      usePagination: false,
      selectedPrimaryColumns: [],
      displayColumnMap: {},
      autoMatchRules: [],
      fieldSearch: "",
      fieldStatusFilter: "all",
    };
  },
  computed: {
    isEndpointMode() {
      return (this.dataSourceType || "ENDPOINT").toUpperCase() === "ENDPOINT";
    },
    isPostEndpoint() {
      return this.isEndpointMode && this.endpointMethod === "POST";
    },
    schemaColumns() {
      const schema = Array.isArray(this.entity?.schema_definition) ? this.entity.schema_definition : [];
      const names = schema.map((item) => item?.name).filter(Boolean);
      const selected = names
        .filter((column) => this.isDisplayColumn(column))
        .sort((left, right) => {
          const leftPrimary = this.isPrimaryColumn(left) ? 1 : 0;
          const rightPrimary = this.isPrimaryColumn(right) ? 1 : 0;

          if (leftPrimary !== rightPrimary) {
            return rightPrimary - leftPrimary;
          }

          const leftOrder = this.displayOrder(left);
          const rightOrder = this.displayOrder(right);

          if (leftOrder !== rightOrder) {
            return leftOrder - rightOrder;
          }

          return left.localeCompare(right);
        });
      const unselected = names
        .filter((column) => !this.isDisplayColumn(column))
        .sort((left, right) => {
          const leftPrimary = this.isPrimaryColumn(left) ? 1 : 0;
          const rightPrimary = this.isPrimaryColumn(right) ? 1 : 0;

          if (leftPrimary !== rightPrimary) {
            return rightPrimary - leftPrimary;
          }

          return left.localeCompare(right);
        });

      return [...selected, ...unselected];
    },
    selectedDisplayColumns() {
      return Object.keys(this.displayColumnMap)
        .filter((column) => this.displayColumnMap[column]?.enabled)
        .sort((left, right) => this.displayOrder(left) - this.displayOrder(right));
    },
    filteredSchemaColumns() {
      const search = (this.fieldSearch || "").toLowerCase();

      return this.schemaColumns.filter((column) => {
        const isSelected = this.isDisplayColumn(column);

        if (this.fieldStatusFilter === "selected" && !isSelected) {
          return false;
        }

        if (this.fieldStatusFilter === "unselected" && isSelected) {
          return false;
        }

        if (!search) {
          return true;
        }

        return column.toLowerCase().includes(search);
      });
    },
  },
  methods: {
    syncFromEntity() {
      this.fieldSearch = "";
      this.fieldStatusFilter = "all";

      this.dataSourceType = ((this.entity?.data_source_type || "ENDPOINT").toString() || "ENDPOINT").toUpperCase();
      this.endpointPath = (this.entity?.endpoint_path || "").toString();
      this.endpointMethod = ((this.entity?.endpoint_method || "GET").toString() || "GET").toUpperCase();
      const endpointBody = this.entity?.endpoint_body;
      this.endpointBody = endpointBody && typeof endpointBody === "object"
        ? JSON.stringify(endpointBody, null, 2)
        : (endpointBody || "").toString();
      this.usePagination = Boolean(this.entity?.use_pagination);
      this.selectedPrimaryColumns = Array.isArray(this.entity?.primary_key_columns)
        ? [...this.entity.primary_key_columns]
        : [];

      const displayColumns = Array.isArray(this.entity?.display_columns) ? this.entity.display_columns : [];
      const map = {};

      displayColumns.forEach((item) => {
        if (!item?.column) {
          return;
        }

        map[item.column] = {
          enabled: true,
          order: Number.isFinite(Number(item.order)) ? Number(item.order) : 0,
          label: item.label || item.column,
        };
      });

      this.displayColumnMap = map;
      this.autoMatchRules = Array.isArray(this.entity?.auto_match_rules)
        ? this.entity.auto_match_rules
            .filter((item) => item && (item.erp_field || item.sm_field))
            .map((item) => ({
              erp_field: (item.erp_field || "").toString(),
              sm_field: (item.sm_field || "").toString(),
            }))
        : [];
    },
    isPrimaryColumn(column) {
      return this.selectedPrimaryColumns.includes(column);
    },
    togglePrimaryColumn(column, checked) {
      if (checked && !this.selectedPrimaryColumns.includes(column)) {
        this.selectedPrimaryColumns.push(column);
        return;
      }

      if (!checked) {
        this.selectedPrimaryColumns = this.selectedPrimaryColumns.filter((item) => item !== column);
      }
    },
    isDisplayColumn(column) {
      return Boolean(this.displayColumnMap[column]?.enabled);
    },
    toggleDisplayColumn(column, checked) {
      if (!checked) {
        this.displayColumnMap = {
          ...this.displayColumnMap,
          [column]: {
            ...(this.displayColumnMap[column] || {}),
            enabled: false,
          },
        };
        return;
      }

      const fallbackOrder = Object.values(this.displayColumnMap).filter((item) => item?.enabled).length;
      this.displayColumnMap = {
        ...this.displayColumnMap,
        [column]: {
          enabled: true,
          order: Number.isFinite(Number(this.displayColumnMap[column]?.order))
            ? Number(this.displayColumnMap[column].order)
            : fallbackOrder,
          label: this.displayColumnMap[column]?.label || column,
        },
      };
    },
    displayOrder(column) {
      const order = this.displayColumnMap[column]?.order;
      return Number.isFinite(Number(order)) ? Number(order) : 0;
    },
    setDisplayOrder(column, value) {
      this.displayColumnMap = {
        ...this.displayColumnMap,
        [column]: {
          ...(this.displayColumnMap[column] || { enabled: true, label: column }),
          order: Number.isFinite(Number(value)) ? Number(value) : 0,
        },
      };
    },
    removeDisplayColumn(column) {
      this.toggleDisplayColumn(column, false);
    },
    addAutoMatchRule() {
      this.autoMatchRules = [...this.autoMatchRules, { erp_field: "", sm_field: "" }];
    },
    updateAutoMatchRule(index, key, value) {
      this.autoMatchRules = this.autoMatchRules.map((item, itemIndex) => {
        if (itemIndex !== index) {
          return item;
        }

        return {
          ...item,
          [key]: (value || "").toString(),
        };
      });
    },
    removeAutoMatchRule(index) {
      this.autoMatchRules = this.autoMatchRules.filter((_, itemIndex) => itemIndex !== index);
    },
    emitDetectSchema() {
      this.$emit("detect-schema");
    },
    emitSave() {
      let endpointBody = null;

      if (this.isPostEndpoint && this.endpointBody.trim()) {
        try {
          endpointBody = JSON.parse(this.endpointBody);
        } catch (error) {
          notify(this.$t("tools.erp_integration.endpoint_body_invalid"), "error", 3000);
          return;
        }
      }

      const displayColumns = Object.keys(this.displayColumnMap)
        .filter((column) => this.displayColumnMap[column]?.enabled)
        .map((column) => ({
          column,
          label: this.displayColumnMap[column]?.label || column,
          order: Number.isFinite(Number(this.displayColumnMap[column]?.order))
            ? Number(this.displayColumnMap[column].order)
            : 0,
        }))
        .sort((a, b) => a.order - b.order);

      const autoMatchRules = this.autoMatchRules
        .map((item) => ({
          erp_field: (item?.erp_field || "").toString().trim(),
          sm_field: (item?.sm_field || "").toString().trim(),
        }))
        .filter((item) => item.erp_field && item.sm_field);

      this.$emit("save", {
        data_source_type: this.dataSourceType,
        endpoint_path: this.isEndpointMode ? this.endpointPath : null,
        endpoint_method: this.isEndpointMode ? this.endpointMethod : "GET",
        endpoint_body: this.isPostEndpoint ? endpointBody : null,
        use_pagination: this.isPostEndpoint ? this.usePagination : false,
        primary_key_columns: this.selectedPrimaryColumns,
        display_columns: displayColumns,
        auto_match_rules: autoMatchRules,
      });
    },
  },
  watch: {
    entity: {
      immediate: true,
      handler() {
        this.syncFromEntity();
      },
    },
  },
};
</script>

<style scoped>
.card-block {
  border: 1px solid #dbe4ef;
  border-radius: 10px;
  background: #fff;
  padding: 14px;
  margin-bottom: 14px;
}

.settings-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.settings-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #1b3559;
}

.settings-actions {
  display: flex;
  gap: 8px;
}

.auto-match-wrapper {
  border: 1px solid #dbe4ef;
  border-radius: 8px;
  padding: 10px;
  background: #fafcff;
}

.auto-match-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.auto-match-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #1b3559;
}

.auto-match-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.auto-match-row {
  display: grid;
  grid-template-columns: minmax(180px, 1fr) minmax(180px, 1fr) auto;
  gap: 8px;
}

@media (max-width: 768px) {
  .auto-match-row {
    grid-template-columns: 1fr;
  }
}

.schema-table-wrapper {
  max-height: 360px;
  overflow: auto;
}

.endpoint-body {
  font-family: monospace;
}

.schema-table {
  margin-bottom: 0;
}

.selected-columns-summary {
  margin-bottom: 10px;
  font-size: 13px;
  color: #425066;
}

.selected-columns-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.schema-filter-row {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) 220px;
  gap: 8px;
  margin-bottom: 10px;
}

.schema-filter-select {
  max-width: 220px;
}

@media (max-width: 768px) {
  .schema-filter-row {
    grid-template-columns: 1fr;
  }

  .schema-filter-select {
    max-width: none;
  }
}

.selected-column-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #c6d4e7;
  border-radius: 999px;
  background: #fff;
  color: #1b3559;
  padding: 4px 10px;
  font-size: 12px;
}

.selected-column-chip__remove {
  color: #8a97a8;
  font-weight: 700;
}

.checkbox-col {
  width: 140px;
  text-align: center;
}

.order-col {
  width: 110px;
}
</style>
